// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import Matrix3 from '../../../math/Matrix3';
import Vector3 from '../../../math/Vector3';
import Vector4 from '../../../math/Vector4';
import Transform from '../../../math/Transform';
import MeshData from '../../../renderer/MeshData';
import Material from '../../../renderer/Material';
import MeshRendererComponent from '../../../entities/components/MeshRendererComponent';
import Component from '../../../entities/components/Component';
import ParticleData from '../../../addons/particlepack/ParticleData';
import Capabilities from '../../../renderer/Capabilities';
import Quad from '../../../shapes/Quad';
import ConstantCurve from '../../../addons/particlepack/curves/ConstantCurve';
import ObjectUtils from '../../../util/ObjectUtils';
import ParticleShaderLib from '../ParticleShaderLib';
import Curve from '../curves/Curve';
import ParticleRendererComponent from './ParticleRendererComponent';
import SystemBus from '../../../entities/SystemBus';
// Polyfill, needed for CocoonJS
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/cbrt
Math.cbrt = Math.cbrt || function (x) {
  const y = Math.pow(Math.abs(x), 1 / 3);
  return x < 0 ? -y : y;
};

function mod(a, b) {
  return ((a % b) + b) % b;
}

const defines = ObjectUtils.clone(ParticleShaderLib.particle.defines);
const definedKeys = {
  COLOR_CURVE_CODE: 'COLOR_CURVE_CODE',
  VELOCITY_CURVE_CODE: 'VELOCITY_CURVE_CODE',
  ROTATION_CURVE_CODE: 'ROTATION_CURVE_CODE',
  SIZE_CURVE_CODE: 'SIZE_CURVE_CODE',
  START_ROTATION_CURVE_CODE: 'START_ROTATION_CURVE_CODE',
  START_COLOR_CODE: 'START_COLOR_CODE',
  START_LIFETIME_CODE: 'START_LIFETIME_CODE',
  START_SIZE_CODE: 'START_SIZE_CODE',
  TEXTURE_FRAME_CODE: 'TEXTURE_FRAME_CODE',
  WORLD_VELOCITY_CURVE_CODE: 'WORLD_VELOCITY_CURVE_CODE',
};

/**
 * A Particle System component simulates things like clouds and flames by generating and animating large numbers of
 * small 2D images in the scene.
 * @example
 * var particleComponent = new ParticleSystemComponent({
 *     loop: true,
 *     preWarm: true,
 *     shapeType: 'sphere',
 *     sphereRadius: 0.5
 * });
 * var entity = world.createEntity([0, 0, 0], particleComponent).addToWorld();
 */
class ParticleSystemComponent extends Component {
  /**
   * @param {Object} [options] Particle options
   * @param {boolean} [options.billboard=true]
   * @param {boolean} [options.depthTest=true]
   * @param {boolean} [options.depthWrite=true]
   * @param {boolean} [options.loop=true]
   * @param {boolean} [options.paused=false]
   * @param {boolean} [options.preWarm=false]
   * @param {boolean} [options.randomDirection=false]
   * @param {boolean} [options.sphereEmitFromShell=false]
   * @param {Curve} [options.colorOverLifetime]
   * @param {Curve} [options.localVelocityOverLifetime]
   * @param {Curve} [options.rotationSpeedOverLifetime]
   * @param {Curve} [options.sizeOverLifetime]
   * @param {Curve} [options.startAngle]
   * @param {Curve} [options.startColor]
   * @param {Curve} [options.startLifetime]
   * @param {Curve} [options.startSize]
   * @param {Curve} [options.startSpeed]
   * @param {Curve} [options.textureFrameOverLifetime]
   * @param {Curve} [options.worldVelocityOverLifetime]
   * @param {number} [options.blending='NoBlending']
   * @param {number} [options.coneAngle] Default is pi/8
   * @param {number} [options.coneLength=1]
   * @param {number} [options.coneRadius=1]
   * @param {number} [options.discardThreshold=0]
   * @param {number} [options.duration=5]
   * @param {number} [options.localSpace=true]
   * @param {number} [options.maxParticles=100]
   * @param {number} [options.renderQueue=3010]
   * @param {number} [options.rotationSpeedScale=1]
   * @param {number} [options.seed=-1]
   * @param {number} [options.startAngleScale=1]
   * @param {number} [options.startSizeScale=1]
   * @param {number} [options.texture]
   * @param {number} [options.textureAnimationCycles=1]
   * @param {number} [options.textureTilesX=1]
   * @param {number} [options.textureTilesY=1]
   * @param {number} [options.time=0]
   * @param {string} [options.coneEmitFrom='base']
   * @param {string} [options.shapeType='cone']
   * @param {Vector3} [options.boxExtents] Default is new Vector3(1,1,1)
   * @param {Vector3} [options.gravity]  Default is zero
   * @param {Vector4} [options.startColorScale] Default is new Vector4(1,1,1,1)
   */
  constructor(options) {
    super();
    options = options || {};
    this.type = ParticleSystemComponent.type;

    this.material = new Material(ObjectUtils.clone(ParticleShaderLib.particle));
    this.material.cullState.enabled = false;
    this.material.uniforms.textureTileInfo = [1, 1, 1, 0];

    ObjectUtils.extend(this.material.uniforms, {
      textureTileInfo: [1, 1, 1, 0],
      invWorldRotation: [1, 0, 0, 0, 1, 0, 0, 0, 1],
      worldRotation: [1, 0, 0, 0, 1, 0, 0, 0, 1],
      gravity: [0, 0, 0],
      uColor: [1, 1, 1, 1],
    });

    this._nextEmitParticleIndex = 0;
    this._localGravity = new Vector3();
    this._worldToLocalRotation = new Matrix3();
    this._localToWorldRotation = new Matrix3();
    this._particleWorldTransform = new Transform();

    // REVIEW: the camera will not be set when this component is added to an
    // entity after the scene has been started, until the camera is changed.
    SystemBus.addListener('sumerian.setCurrentCamera', (camConfig) => {
      this._camera = camConfig.camera;
    });

    /**
     * The entity which the component is attached on. Will be set when the component is attached to the entity.
     * @type {(Entity|null)}
     * @readonly
     */
    this.entity = null;

    /**
     * Use the pause/play/stop methods if you want to modify the state.
     * @type {boolean}
     * @default false
     * @readonly
     */
    this.paused = options.paused !== undefined ? options.paused : false;

    /**
     * Whether to start playing when the ParticleSystemSystem plays.
     * @type {boolean}
     * @default true
     */
    this.autoPlay = options.autoPlay !== undefined ? options.autoPlay : true;

    /**
     * The current particles in the system.
     * @type {Array<ParticleData>}
     * @readonly
     */
    this.particles = [];


    /**
     * Current time in the system.
     * @type {number}
     * @readonly
     */
    this.time = options.time || 0;

    /**
     * Force that makes particles fall.
     * @type {Vector3}
     */
    this.gravity = options.gravity ? options.gravity.clone() : new Vector3();

    /**
     * Extents of the box, if box shape is used. Read only. To change it, see the method setBoxExtents().
     * @type {Vector3}
     * @readonly
     */
    this.boxExtents = options.boxExtents ? options.boxExtents.clone() : new Vector3(1, 1, 1);

    /**
     * Acts as a scale on the color curve. Should be used at runtime.
     * @type {Vector4}
     */
    this.startColorScale = options.startColorScale ? options.startColorScale.clone() : new Vector4(1, 1, 1, 1);

    this._default = {
      emissionRate: new ConstantCurve({ value: 10 }),
      preWarm: false,
      seed: Math.floor(Math.random() * 32768),
      shapeType: 'cone',
      sphereRadius: 1,
      sphereEmitFromShell: false,
      randomDirection: false,
      coneEmitFrom: 'base', // base, volume, volumeshell,
      coneRadius: 1,
      coneAngle: Math.PI / 8,
      coneLength: 1,
      startColor: null,
      colorOverLifetime: null,
      duration: 5,
      localSpace: true,
      startSpeed: new ConstantCurve({ value: 5 }),
      localVelocityOverLifetime: null,
      worldVelocityOverLifetime: null,
      maxParticles: 100,
      startLifetime: new ConstantCurve({ value: 5 }),
      renderQueue: 3010,
      discardThreshold: 0,
      loop: true,
      blending: 'NoBlending',
      depthWrite: true,
      depthTest: true,
      textureTilesX: 1,
      textureTilesY: 1,
      textureAnimationCycles: 1,
      sortMode: ParticleSystemComponent.SORT_NONE,
      mesh: new Quad(),
      billboard: true,
      textureFrameOverLifetime: null,
      startSize: null,
      startSizeScale: 1,
      sizeOverLifetime: null,
      startAngle: null,
      startAngleScale: 1,
      rotationSpeedOverLifetime: null,
      rotationSpeedScale: 1,
      texture: null,
      boundsRadius: Number.MAX_VALUE,
    };

    ObjectUtils.copyOptions(this, options, this._default);
    
    this.material.makeInstanced(this._maxParticles < Capabilities.maxInstanceDraw ? this._maxParticles : Capabilities.maxInstanceDraw);
  }

  /**
   * If set to true, the particles will always face the camera.
   * @memberof ParticleSystemComponent
   * @type {boolean}
   */
  get billboard() {
    return this.material.shader.hasDefine('BILLBOARD');
  }

  set billboard(value) {
    const shader = this.material.shader;
    value = !!value;
    if (value) {
      shader.setDefine('BILLBOARD', true);
    } else {
      shader.removeDefine('BILLBOARD');
    }
  }

  /**
   * What type of blending to use for the particle mesh.
   * @memberof ParticleSystemComponent
   * @type {string}
   */
  get blending() {
    return this.material.blendState.blending;
  }

  set blending(value){
    this.material.blendState.blending = value || this._default.blending;
  }

  /**
   * Color of particles, as a curve over their life time.
   * @memberof ParticleSystemComponent
   * @type {(Vector4Curve|null)}
   */
  get colorOverLifetime() {
    return this._colorOverLifetime;
  }

  set colorOverLifetime(value) {
    this._setCurveAndDefinedCode('_colorOverLifetime', value, definedKeys.COLOR_CURVE_CODE, false);
  }

  get sphereRadius() {
    return this._sphereRadius;
  }

  set sphereRadius(value) {
    value = typeof value === 'number' && value > 0 ? value : this._default.sphereRadius;
    if (this._sphereRadius !== value) {
      this._sphereRadius = value;
      if (this.shapeType === 'sphere') {
        this._particleDataNeedsInit = true;
      }
    }
  }

  /**
   * Angle of the cone, if cone shape is used.
   * @memberof ParticleSystemComponent
   * @type {number}
   */
  get coneAngle() {
    return this._coneAngle;
  }

  set coneAngle(value) {
    value = typeof value === 'number' && value > 0 ? value : this._default.coneAngle;
    if (this._coneAngle !== value) {
      this._coneAngle = value;
      if (this.shapeType === 'cone') {
        this._particleDataNeedsInit = true;
      }
    }
  }

  /**
   * Where to emit from, if using the cone shape. Set to 'base', 'volume' or 'volumeshell'.
   * @memberof ParticleSystemComponent
   * @type {string}
   */
  get coneEmitFrom() {
    return this._coneEmitFrom;
  }

  set coneEmitFrom(value) {
    value = value === 'base' || value === 'volume' || value === 'volumeshell' ? value : this._default.coneEmitFrom;
    if (this._coneEmitFrom !== value) {
      this._coneEmitFrom = value;
      if (this.shapeType === 'cone') {
        this._particleDataNeedsInit = true;
      }
    }
  }

  /**
   * Length of the cone, if cone shape is used.
   * @memberof ParticleSystemComponent
   * @type {number}
   */
  get coneLength() {
    return this._coneLength;
  }

  set coneLength(value) {
    value = typeof value === 'number' && value > 0 ? value : this._default.coneLength;
    if (this._coneLength !== value) {
      this._coneLength = value;
      if (this.shapeType === 'cone') {
        this._particleDataNeedsInit = true;
      }
    }
  }

  /**
   * Radius of the cone, if cone shape is used.
   * @memberof ParticleSystemComponent
   * @type {number}
   */
  get coneRadius() {
    return this._coneRadius;
  }

  set coneRadius(value) {
    value = typeof value === 'number' && value > 0 ? value : this._default.coneRadius;
    if (this._coneRadius !== value) {
      this._coneRadius = value;
      if (this.shapeType === 'cone') {
        this._particleDataNeedsInit = true;
      }
    }
  }

  /**
   * @memberof ParticleSystemComponent
   * @type {boolean}
   */
  get depthTest() {
    return this.material.depthState.enabled;
  }

  set depthTest(value) {
    this.material.depthState.enabled = !!value;
  }

  /**
   * @memberof ParticleSystemComponent
   * @type {boolean}
   */
  get depthWrite() {
    return this.material.depthState.write;
  }

  set depthWrite(value) {
    this.material.depthState.write = !!value;
  }

  /**
   * At what alpha threshold should the fragments be discarded?
   * @memberof ParticleSystemComponent
   * @type {number}
   */
  get discardThreshold() {
    return this.material.uniforms.discardThreshold;
  }

  set discardThreshold(value) {
    this.material.uniforms.discardThreshold = typeof value === 'number' ? value : this._default.discardThreshold;
  }

  /**
   * The time for a full animation cycle of the emission.
   * @memberof ParticleSystemComponent
   * @type {number}
   */
  get duration() {
    return this.material.uniforms.duration;
  }

  set duration(value) {
    value = typeof value === 'number' && value > 0 ? value : this._default.duration;
    if (this.material.uniforms.duration !== value) {
      this.material.uniforms.duration = value;
      this._particleDataNeedsInit = true;
    }
  }

  /**
   * Number of particles to emit per second
   * @memberof ParticleSystemComponent
   * @type {Curve}
   */
  get emissionRate() {
    return this._emissionRate;
  }

  set emissionRate(value) {
    value = value instanceof Curve ? value : this._default.emissionRate;
    if (this._emissionRate !== value) {
      this._emissionRate = value.clone();
      this._particleDataNeedsInit = true;
    }
  }

  /**
   * If set to true, the partiles will be simulated in local entity space. If set to false, world space is used.
   * @memberof ParticleSystemComponent
   * @type {boolean}
   */
  get localSpace() {
    return this._localSpace;
  }

  set localSpace(value) {
    value = !!value;
    if (this._localSpace !== value) {
      this._localSpace = value;
      if (this.meshEntity) {
        const transformComponent = this.meshEntity.transformComponent;
        transformComponent.transform.setIdentity();
        transformComponent.setUpdated();
      }
      this._particleDataNeedsInit = true;
    }
  }

  /**
   * The velocity of particles in local particle space.
   * @memberof ParticleSystemComponent
   * @type {(Vector3Curve|null)}
   */
  get localVelocityOverLifetime() {
    return this._localVelocityOverLifetime;
  }

  set localVelocityOverLifetime(value) {
    this._setCurveAndDefinedCode('_localVelocityOverLifetime', value, definedKeys.VELOCITY_CURVE_CODE, true);
  }

  /**
   * Whether to loop the particle emission after one duration cycle.
   * @memberof ParticleSystemComponent
   * @type {boolean}
   */
  get loop() {
    return this._loop;
  }

  set loop(value) {
    value = !!value;
    if (this._loop !== value) {
      this._loop = value;
      this._particleDataNeedsInit = true;
      this.time = 0;
    }
  }

  /**
   * Maximum number of particles visible at the same time.
   * @memberof ParticleSystemComponent
   * @type {number}
   */
  get maxParticles() {
    return this._maxParticles;
  }

  set maxParticles(value) {
    value = typeof value === 'number' && value > 0 ? Math.ceil(value) : this._default.maxParticles;
    if (this._maxParticles !== value) {
      this._maxParticles = value;
      this.material.makeInstanced(this._maxParticles < Capabilities.maxInstanceDraw ? this._maxParticles : Capabilities.maxInstanceDraw);
      this._particleDataNeedsInit = true;
    }
  }

  /**
   * @memberof ParticleSystemComponent
   * @type {MeshData}
   */
  get mesh() {
    return this._mesh;
  }

  set mesh(value) {
    value = value || this._default.mesh;
    if (this._mesh !== value) {
      this._mesh = value;
      if (this.meshEntity) {
        this.meshEntity.meshDataComponent.meshData = this._mesh;
        this._particleDataNeedsInit = true;
      }
    }
  }

  /**
   * Pre-warm the emission (fast forward time one duration). Not available if looping is on.
   * @memberof ParticleSystemComponent
   * @type {boolean}
   */
  get preWarm() {
    return this._preWarm;
  }

  set preWarm(value) {
    value = !!value;
    if (this._preWarm !== value) {
      this._preWarm = value;
      if (this._preWarm) {
        this.material.shader.removeDefine('HIDE_IF_EMITTED_BEFORE_ZERO');
      } else {
        this.material.shader.setDefine('HIDE_IF_EMITTED_BEFORE_ZERO', true);
      }
    }
  }

  /**
   * Emit in random directions, instead of in the emitter volume direction.
   * @memberof ParticleSystemComponent
   * @type {boolean}
   */
  get randomDirection() {
    return this._randomDirection;
  }

  set randomDirection(value) {
    value = !!value;
    if (this._randomDirection !== value) {
      this._randomDirection = value;
      this._particleDataNeedsInit = true;
    }
  }

  /**
   * @memberof ParticleSystemComponent
   * @type {number}
   */
  get renderQueue() {
    return this.material.renderQueue;
  }

  set renderQueue(value) {
    this.material.renderQueue = typeof value === 'number' && value > 0 ? value : this._default.renderQueue;
  }

  /**
   * The rotation speed in radians per second, specified using a curve over the particle life time.
   * @memberof ParticleSystemComponent
   * @type {(Curve|null)}
   */
  get rotationSpeedOverLifetime() {
    return this._rotationSpeedOverLifetime;
  }

  set rotationSpeedOverLifetime(value) {
    this._setCurveAndDefinedCode('_rotationSpeedOverLifetime', value, definedKeys.ROTATION_CURVE_CODE, true);
  }

  /**
   * Acts as a scale on the rotationSpeed curve.
   * @memberof ParticleSystemComponent
   * @type {number}
   */
  get rotationSpeedScale() {
    return this.material.uniforms.uRotationSpeed;
  }

  set rotationSpeedScale(value) {
    this.material.uniforms.uRotationSpeed = typeof value === 'number' ? value : this._default.rotationSpeedScale;
  }

  /**
   * Randomization seed.
   * @memberof ParticleSystemComponent
   * @type {number}
   */
  get seed() {
    return this._initSeed;
  }

  set seed(value) {
    value = typeof value === 'number' && value > 0 ? value : this._default.seed;
    if (this._initSeed !== value) {
      this._initSeed = value;
      this._seed = value;
      this._particleDataNeedsInit = true;
    }
  }

  /**
   * Emitter volume. Set to 'sphere', 'cone', or 'box'.
   * @memberof ParticleSystemComponent
   * @type {string}
   */
  get shapeType() {
    return this._shapeType;
  }

  set shapeType(value) {
    value = value === 'sphere' || value === 'cone' || value === 'box' ? value : this._default.shapeType;
    if (this._shapeType !== value) {
      this._shapeType = value;
      this._particleDataNeedsInit = true;
    }
  }

  /**
   * This curve alters the size of particles over their life time.
   * @memberof ParticleSystemComponent
   * @type {(Curve|null)}
   */
  get sizeOverLifetime() {
    return this._sizeOverLifetime;
  }

  set sizeOverLifetime(value) {
    this._setCurveAndDefinedCode('_sizeOverLifetime', value, definedKeys.SIZE_CURVE_CODE, false);
  }

  /**
   * @memberof ParticleSystemComponent
   * @type {string}
   */
  get sortMode() {
    return this._sortMode;
  }

  set sortMode(value) {
    this._sortMode = value === ParticleSystemComponent.SORT_CAMERA_DISTANCE || value === ParticleSystemComponent.SORT_NONE ? value : this._default.sortMode;
  }

  /**
   * Whether to emit from the sphere shell, if sphere shape is used.
   * @memberof ParticleSystemComponent
   * @type {boolean}
   */
  get sphereEmitFromShell() {
    return this._sphereEmitFromShell;
  }

  set sphereEmitFromShell(value) {
    value = !!value;
    if (this._sphereEmitFromShell !== value) {
      this._sphereEmitFromShell = value;
      if (this.shapeType === 'sphere') {
        this._particleDataNeedsInit = true;
      }
    }
  }

  /**
   * The initial angle of particles, as a curve over the emitter duration.
   * @memberof ParticleSystemComponent
   * @type {(Curve|null)}
   */
  get startAngle() {
    return this._startAngle;
  }

  set startAngle(value) {
    this._setCurveAndDefinedCode('_startAngle', value, definedKeys.START_ROTATION_CURVE_CODE, false);
  }

  /**
   * Acts as a scale on the startAngle curve.
   * @memberof ParticleSystemComponent
   * @type {number}
   */
  get startAngleScale() {
    return this.material.uniforms.uStartAngle;
  }

  set startAngleScale(value) {
    this.material.uniforms.uStartAngle = typeof value === 'number' ? value : this._default.startAngleScale;
  }

  /**
   * The initial color of particles as a color curve over the emitter duration.
   * @memberof ParticleSystemComponent
   * @type {(Vector4Curve|null)}
   */
  get startColor() {
    return this._startColor;
  }

  set startColor(value) {
    this._setCurveAndDefinedCode('_startColor', value, definedKeys.START_COLOR_CODE, false);
  }

  /**
   * Initial life time of particles, as a curve over the emitter duration.
   * @memberof ParticleSystemComponent
   * @type {(Curve|null)}
   */
  get startLifetime() {
    return this._startLifetime;
  }

  set startLifetime(value) {
    this._setCurveAndDefinedCode('_startLifetime', value, definedKeys.START_LIFETIME_CODE, false);
  }

  /**
   * Initial size of particles, as a curve over the emitter duration.
   * @memberof ParticleSystemComponent
   * @type {(Curve|null)}
   */
  get startSize() {
    return this._startSize;
  }

  set startSize(value) {
    this._setCurveAndDefinedCode('_startSize', value, definedKeys.START_SIZE_CODE, false);
  }

  /**
   * Acts as a scale on the startSize curve.
   * @memberof ParticleSystemComponent
   * @type {number}
   */
  get startSizeScale() {
    return this.material.uniforms.uStartSize;
  }

  set startSizeScale(value) {
    this.material.uniforms.uStartSize = typeof value === 'number' ? value : this._default.startSizeScale;
  }

  /**
   * Initial speed of the particles, described by a curve over the emitter duration.
   * @memberof ParticleSystemComponent
   * @type {(Curve|null)}
   */
  get startSpeed() {
    return this._startSpeed;
  }

  set startSpeed(value) {
    value = value instanceof Curve ? value : this._default.startSpeed;
    if (this._startSpeed !== value) {
      this._startSpeed = value.clone();
      this._particleDataNeedsInit = true;
    }
  }

  /**
   * A texture for the particles.
   * @memberof ParticleSystemComponent
   * @type {(Texture|null)}
   */
  get texture() {
    return this.material.getTexture('PARTICLE_TEXTURE');
  }

  set texture(value) {
    const material = this.material;
    const shader = material.shader;
    if (value) {
      material.setTexture('PARTICLE_TEXTURE', value);
      shader.setDefine('PARTICLE_TEXTURE', true);
    } else {
      material.removeTexture('PARTICLE_TEXTURE');
      shader.removeDefine('PARTICLE_TEXTURE');
    }
  }

  /**
   * How fast the texture animation should cycle. Acts as a scale on the textureFrameOverLifetime curve.
   * @memberof ParticleSystemComponent
   * @type {number}
   */
  get textureAnimationCycles() {
    return this.material.uniforms.textureTileInfo[2];
  }

  set textureAnimationCycles(value) {
    this.material.uniforms.textureTileInfo[2] = typeof value === 'number' ? value : this._default.textureAnimationCycles;
  }

  /**
   * The current texture frame, given by a curve over the particle life time.
   * @memberof ParticleSystemComponent
   * @type {(Curve|null)}
   */
  get textureFrameOverLifetime() {
    return this._textureFrameOverLifetime;
  }

  set textureFrameOverLifetime(value) {
    this._setCurveAndDefinedCode('_textureFrameOverLifetime', value, definedKeys.TEXTURE_FRAME_CODE, false);
  }

  /**
   * Texture tiling in the X direction.
   * @memberof ParticleSystemComponent
   * @type {number}
   */
  get textureTilesX() {
    return this.material.uniforms.textureTileInfo[0];
  }

  set textureTilesX(value) {
    this.material.uniforms.textureTileInfo[0] = typeof value === 'number' && value > 0 ? value : this._default.textureTilesX;
  }

  /**
   * Texture tiling in the Y direction.
   * @memberof ParticleSystemComponent
   * @type {number}
   */
  get textureTilesY() {
    return this.material.uniforms.textureTileInfo[1];
  }

  set textureTilesY(value) {
    this.material.uniforms.textureTileInfo[1] = typeof value === 'number' && value > 0 ? value : this._default.textureTilesY;
  }

  /**
   * Velocity of particles in world space.
   * @memberof ParticleSystemComponent
   * @type {(Vector3Curve|null)}
   */
  get worldVelocityOverLifetime() {
    return this._worldVelocityOverLifetime;
  }

  set worldVelocityOverLifetime(value) {
    this._setCurveAndDefinedCode('_worldVelocityOverLifetime', value, definedKeys.WORLD_VELOCITY_CURVE_CODE, true);
  }

  /**
   * Set local curve variables based on the input value
   * @param {string} localVariable the name of local variable
   * @param {Curve|null} value the input value for the curve
   * @param {string} definedKey the define key of the curve code
   * @param {boolean} integralToGLSL is it calling toGLSL or integralTOGLSL
   * @private
   */
  _setCurveAndDefinedCode(localVariable, value, definedKey, integralToGLSL) {
    if (value instanceof Curve) {
      this[localVariable] = value.clone();
      if (integralToGLSL) {
        this.material.shader.setDefine(definedKey, this[localVariable].integralToGLSL('t', 'emitRandom'));
      } else {
        this.material.shader.setDefine(definedKey, this[localVariable].toGLSL('t', 'emitRandom'));
      }
    } else if (!value) {
      this[localVariable] = null;
      this.material.shader.setDefine(definedKey, defines[definedKey]);
    }
  }

  /**
   * Set the boxExtents.
   * @param {Vector3} extents
   */
  setBoxExtents(extents) {
    this.boxExtents.copy(extents);
    if (this.shapeType === 'box') {
      this._particleDataNeedsInit = true;
    }
  }

  /**
   * @private
   */
  _random() {
    const x = Math.sin(this._seed++) * 100000;
    return x - Math.floor(x);
  }

  /**
   * @private
   */
  _updateUniforms() {
    const uniforms = this.material.uniforms;

    // Gravity in local space
    const worldToLocalRotation = this._worldToLocalRotation;
    const localToWorldRotation = this._localToWorldRotation;

    if (this.localSpace) {
      // In local space:
      // 1. Need to multiply the worldVelocity with the inverse rotation, to get local velocity
      // 2. World velocity is good as it is
      worldToLocalRotation.copyMatrix4(this._particleWorldTransform.matrix).invert();
      localToWorldRotation.copy(Matrix3.IDENTITY);
    } else {
      // 1. Need to multiply the localVelocity with the world rotation, to get local velocity
      // 2. Local velocity is good as it is
      worldToLocalRotation.copy(Matrix3.IDENTITY);
      localToWorldRotation.copyMatrix4(this._particleWorldTransform.matrix);
    }

    const localGravity = this._localGravity;
    localGravity.copy(this.gravity);
    localGravity.applyPost(worldToLocalRotation);
    const g = uniforms.gravity;
    g[0] = localGravity.x;
    g[1] = localGravity.y;
    g[2] = localGravity.z;

    for (let i = 0; i < 9; i++) {
      uniforms.invWorldRotation[i] = worldToLocalRotation.data[i]; // will be multiplied with the world velocity
      uniforms.worldRotation[i] = localToWorldRotation.data[i]; // will be multiplied with the local velocity
    }

    uniforms.time = this.time;

    const uColor = uniforms.uColor;
    const colorScale = this.startColorScale;
    uColor[0] = colorScale.x;
    uColor[1] = colorScale.y;
    uColor[2] = colorScale.z;
    uColor[3] = colorScale.w;
  }

  /**
   * Pause the animation.
   */
  pause() {
    this.paused = true;
  }

  /**
   * Resume the animation.
   */
  resume() {
    this.play();
    this._particleDataNeedsInit = true;
  }

  /**
   * Play the animation.
   */
  play() {
    this.paused = false;
    this._particleDataNeedsInit = true;
  }

  /**
   * Stop the animation.
   */
  stop() {
    this.pause();
    this.time = 0;
    this._seed = this._initSeed;
    this._nextEmitParticleIndex = 0;
    this.particles = [];
  }

  /**
   * Sync particles array based on the this.maxParticles.
   * Making sure the number of particle in this.particles is equal to this.maxParticles
   * @private
   */
  _syncParticlesData() {
    const maxParticles = this.maxParticles;
    //If the particles.length < max, fill in particle array until they are equal
    while (this.particles.length < maxParticles) {
      const particle = new ParticleData(this);
      this.particles.push(particle);
    }
    //If the particles.length > max, remove the last particles until they are equal
    if (this.particles.length > maxParticles) {
      this.particles.splice(maxParticles, this.particles.length - maxParticles);
    }
  }

  /**
   * Init this.particles array to initial state
   * @private
   */
  _initParticlesData() {
    if (!this._particleDataNeedsInit) {
      return;
    }

    const duration = this.duration;
    for (let particle of this.particles) {
      particle.emitTime = 0;
      particle.active = 0;
      particle.loopAfter = duration;
    }
    this._particleDataNeedsInit = false;
  }

  /**
   * Get a random position and location inside the current shape.
   * @private
   */
  _generateLocalPositionAndDirection(position, direction, time) {
    const shapeType = this.shapeType;
    const cos = Math.cos;
    const sin = Math.sin;
    const pi = Math.PI;

    if (shapeType === 'sphere') {
      var theta = Math.acos(2 * this._random() - 1);
      var phi = 2 * pi * this._random();
      var r = this.sphereRadius;
      if (!this.sphereEmitFromShell) {
        r *= Math.cbrt(this._random());
      }
      position.setDirect(
        r * cos(phi) * sin(theta),
        r * cos(theta),
        r * sin(phi) * sin(theta)
      );
      direction.setDirect(
        cos(phi) * sin(theta),
        cos(theta),
        sin(phi) * sin(theta)
      );
    } else if (shapeType === 'cone') {
      var phi = 2 * pi * this._random();
      switch (this.coneEmitFrom) {
        case 'base':
        // Somewhere in the base
          var ra = Math.sqrt(this._random());
          var r = this.coneRadius * ra;
          position.setDirect(r * cos(phi), 0, r * sin(phi));

          var r2 = (this.coneRadius + this.coneLength * Math.tan(this.coneAngle)) * ra;
          direction.setDirect(
          r2 * cos(phi),
          this.coneLength,
          r2 * sin(phi)
        ).sub(position);
          break;

        case 'volume':
        // Somewhere in the base
          var ra = Math.sqrt(this._random());
          var r = this.coneRadius * ra;
          position.setDirect(r * cos(phi), 0, r * sin(phi));

          var r2 = (this.coneRadius + this.coneLength * Math.tan(this.coneAngle)) * ra;
          direction.setDirect(
          r2 * cos(phi),
          this.coneLength,
          r2 * sin(phi)
        ).sub(position);

          direction.setDirect(
          r2 * cos(phi),
          this.coneLength,
          r2 * sin(phi),
        );
          position.lerp(direction, this._random());
          direction.sub(position);
          break;

        case 'volumeshell':

          var r = this.coneRadius;
          position.setDirect(r * cos(phi), 0, r * sin(phi));

          var r2 = (this.coneRadius + this.coneLength * Math.tan(this.coneAngle));
          direction.setDirect(
          r2 * cos(phi),
          this.coneLength,
          r2 * sin(phi),
        );
          position.lerp(direction, this._random());
          direction.sub(position);
          break;
      }
    } else {
      // box
      position.setDirect(
        this._random() - 0.5,
        this._random() - 0.5,
        this._random() - 0.5,
      ).mul(this.boxExtents);
      direction.setDirect(0, 1, 0);
    }
    if (this.randomDirection) {
      var theta = Math.acos(2 * this._random() - 1);
      var phi = 2 * pi * this._random();
      direction.setDirect(
        cos(phi) * sin(theta),
        cos(theta),
        sin(phi) * sin(theta),
      );
    }

    const speed = this.startSpeed.getValueAt(time, this._random());
    direction.normalize().scale(speed);
  }

  /**
   * Emit the next particle.
   * @param {Vector3} position
   * @param {Vector3} direction
   */
  emitOne(position, direction) {
    // Get the next emitted particle
    const particle = this.particles[this._nextEmitParticleIndex];
    particle.startPosition.copy(position);
    particle.startDirection.copy(direction);
    particle.active = 1;
    particle.emitRandom = this._random();
    particle.emitTime = this.time; // Emitting NOW
  }

  /**
   * @private
   */
  _updateBounds() {
    if (!this.meshEntity || !this.meshEntity.meshRendererComponent.worldBound) {
      return;
    }
    const bounds = this.meshEntity.meshRendererComponent.worldBound;
    this._particleWorldTransform.applyForward(Vector3.ZERO, bounds.center);
    const r = this.boundsRadius;
    bounds.xExtent = bounds.yExtent = bounds.zExtent = r * 2;
  }

  /**
   * Sort the particles based on its distance to the camera in descent order
   * @param {Array<ParticleData>} particles to be sorted
   * @private
   */
  _sortParticles(particles) {
    if (this.sortMode === ParticleSystemComponent.SORT_NONE) {
      return;
    }

    // Update sort values to represent the distance of particle to the camera.
    // The larger number means further, the smaller means closer
    for (let particle of particles) {
      if (this._camera) {
        particle.sortValue = particle.getWorldPosition(tmpWorldPos).dot(this._camera._direction);
      } else {
        particle.sortValue = -1;
      }
    }

    // sort the particles by camera distance in descent order: the furthest particle will be rendered firstly
    particles.sort((a, b) => {
      return b.sortValue - a.sortValue;
    });
  }

  /**
   * @private
   * @param {number} tpf
   */
  process(tpf) {
    const entity = this.entity;
    //ParticleWorldTransform inherit parent's translation and rotation but not scale;
    this._particleWorldTransform.setIdentity();
    copyPositionAndRotation(this._particleWorldTransform, entity.transformComponent.sync().worldTransform);
    this.meshEntity.meshRendererComponent.hidden = entity.isVisiblyHidden();

    if (this.paused) {
      return;
    }

    this._syncParticlesData();
    this._initParticlesData();

    const lastTime = this.time;
    this.time += tpf;
    const time = this.time;

    if (this.localSpace) {
      // Copy the parent mesh world transform to each particle without inheriting scale
      this.meshEntity.transformComponent.transform.copy(this._particleWorldTransform);
      this.meshEntity.transformComponent.setUpdated();
    }
    // Emit according to emit rate.
    const loop = this.loop;
    const duration = this.duration;
    const normalizedTime = mod(time / this.duration, 1);
    //EmissionRate or startLife should be >= 0.
    const emissionRate = Math.max(0.0, this.emissionRate.getValueAt(normalizedTime, this._random()));
    const startLifeTime = Math.max(0.0, this.startLifetime.getValueAt(normalizedTime, this._random()));
    // numToEmit = emissionRate * time - emissionRate * lastTime
    const numToEmit = !loop && time > duration ? 0 : Math.floor(time * emissionRate) - Math.floor(lastTime * emissionRate);
    for (let i = 0; i < numToEmit; i++) {
      const particleIndex = this._findNextParticleIndex(startLifeTime);
      if (particleIndex === undefined || particleIndex === null) {
        continue;
      } else {
        this._nextEmitParticleIndex = particleIndex;
      }


      // get pos and direction from the shape
      this._generateLocalPositionAndDirection(tmpPos, tmpDir, normalizedTime);
      if (!this.localSpace) {
        // Transform particle start position and direction to world space
        // TODO: interpolation between last and current transforms
        this._particleWorldTransform.applyForward(tmpPos, tmpPos);
        this._particleWorldTransform.applyForwardVector(tmpDir, tmpDir);
      }

      // Emit
      // var interpolationCompensation = (time - lastTime) * (i + 1) / numToEmit;
      this.emitOne(tmpPos, tmpDir);
    }

    const activeParticles = this.particles.filter((particle) => particle && particle.active && time - particle.emitTime <= particle.lifeTime);
    this._sortParticles(activeParticles);
    this.meshEntity.particleRendererComponent.process(activeParticles);
    this._updateUniforms();
    this._updateBounds();
  }

  /**
   * find the next emitted particle index based on the time in the current particle cycle
   * @param {Number} startLifeTime - the current lifeTime of all the particles
   * @returns {Integer|null} the next emitted particle index. Null if does not find any available
   * @private
   */
  _findNextParticleIndex(startLifeTime) {
    const time = this.time;
    const particles = this.particles;
    for (let i = this._nextEmitParticleIndex; i < this._nextEmitParticleIndex + particles.length; i++) {
      const particleIndex = i % particles.length;
      const particle = particles[particleIndex];
      particle.lifeTime = startLifeTime;
      const age = time - particle.emitTime;
      // if the particle is inactive or it runs out of its lifetime
      if (!particle.active || age > particle.lifeTime) {
        return particleIndex;
      }
    }
    return null;
  }

  /**
   * @private
   * @param entity
   */
  attached(entity) {
    this.entity = entity;

    const meshRendererComponent = new MeshRendererComponent(this.material);
    meshRendererComponent.castShadows = meshRendererComponent.receiveShadows = meshRendererComponent.isPickable = meshRendererComponent.isReflectable = false;
    const particleRendererComponent = new ParticleRendererComponent(this.material);
    this.meshEntity = this.entity._world.createEntity(this.mesh.clone(), 'ParticleSystemComponentMesh')
      .set(meshRendererComponent)
      .set(particleRendererComponent)
      .addToWorld();

    this._particleDataNeedsInit = true;
  }

  /**
   * @private
   * @param entity
   */
  detached(entity) {
    this.meshEntity.clearComponent('MeshDataComponent');
    this.particles.length = 0;
    this.meshEntity.removeFromWorld();
    this.entity = this.meshEntity = null;
  }

  /**
   * @private
   * @param obj
   * @param entity
   */
  static applyOnEntity(obj, entity) {
    if (obj instanceof ParticleSystemComponent) {
      entity.setComponent(obj);
    }
  }

  /**
   * @returns {ParticleSystemComponent}
   */
  clone() {
    return new ParticleSystemComponent(this);
  }

  static get type() {
    return 'ParticleSystemComponent';
  }

  /**
   * No sorting of particles.
   * @type {number}
   * @readonly
   */
  static get SORT_NONE() {
    return 1;
  }

  /**
   * Sort particles by camera distance.
   * @type {number}
   * @readonly
   */
  static get SORT_CAMERA_DISTANCE() {
    return 2;
  }

}


const tmpWorldPos = new Vector3();

const tmpPos = new Vector3();
const tmpDir = new Vector3();

const copyPositionAndRotation = (destTransform, srcTransform) => {
  destTransform.rotation.copy(srcTransform.rotation);
  destTransform.translation.set(srcTransform.translation);
  destTransform.update();
};


export default ParticleSystemComponent;
